import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles,
  PenTool,
  Calendar,
  Users,
  Wallet,
  MapPin,
  ArrowRight,
  CheckCircle2,
  Clock,
  Plus,
  Trash2,
  Upload,
  Share2,
  FileText,
  AlertCircle,
  Download,
  Loader2,
  Compass,
  BedDouble,
  Car,
  Copy,
  Check,
  Edit3,
  Save,
  ArrowLeft,
  GitPullRequest,
  ShieldCheck,
  ShieldAlert,
  ShoppingBag,
  HeartPulse,
  AlertTriangle,
  PhoneCall,
  Utensils
} from 'lucide-react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Loader from './Loader';
import tripsApi from '../api/tripsApi';
import { useAuth } from '../context/AuthContext';
import SocialShareModal from './SocialShareModal';

/**
 * Universal helper to normalize AI Planner payloads:
 * Handles direct fields, responseData wrapping, or nested plan payloads
 */
export function normalizeAiPlan(raw) {
  if (!raw) return null;
  const data = raw.data || raw;
  const inner = data.responseData || data.plan?.responseData || data.dynamicTrip?.responseData || data;
  return {
    ...data,
    ...inner,
    summary: inner.summary || data.summary || {},
    days: inner.days || inner.itinerary || data.days || data.itinerary || [],
    costBreakdownFull: inner.costBreakdownFull || data.costBreakdownFull || null,
    totalCostSummary: inner.totalCostSummary || data.totalCostSummary || null,
    travelInsights: inner.travelInsights || data.travelInsights || null,
    logistics: inner.logistics || data.logistics || null,
    survivalGuide: inner.survivalGuide || data.survivalGuide || null,
  };
}

/**
 * Cloudinary Image Uploader Component
 */
function CloudinaryUploader({ label, value, onChange, className = '' }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64Data = event.target?.result;
        const res = await tripsApi.uploadImage(base64Data);
        onChange(res?.url || base64Data);
      } catch (err) {
        console.warn('Cloudinary upload fallback to data URL:', err);
        onChange(event.target?.result);
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider">{label}</label>}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload via Cloudinary..."
          className="flex-1 px-3.5 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] font-bold text-xs rounded-xl border border-teal-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          <span>{uploading ? 'Uploading...' : 'Upload'}</span>
        </button>
      </div>
      {value && (
        <div className="mt-1 h-14 w-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

export default function CreateTrip({ initialMode = 'ai', forcedMode = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draftId');
  const sharedTripIdParam = searchParams.get('sharedTripId');
  const forkTripParam = searchParams.get('forkTrip');

  const { user } = useAuth();

  // Mode: 'ai' = AI Itinerary Architect, 'manual' = Custom Journey Crafter
  const [activeMode, setActiveMode] = useState(forcedMode || initialMode);
  const currentMode = forcedMode || activeMode;
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Loading');
  const [successModal, setSuccessModal] = useState(null); // { type: 'draft'|'shared'|'shared_updated'|'pr_submitted', trip: any, title, message }
  const [copiedCode, setCopiedCode] = useState(false);

  // Draft continuation, shared trip editing & companion PR fork state
  const [editingDraftId, setEditingDraftId] = useState(draftIdParam || null);
  const [editingSharedTripId, setEditingSharedTripId] = useState(sharedTripIdParam || null);
  const [proposeChangesTripId, setProposeChangesTripId] = useState(forkTripParam || null);
  const [prOrganizerName, setPrOrganizerName] = useState('');
  const [prTitle, setPrTitle] = useState('');
  const [prNote, setPrNote] = useState('');
  const [showPrNoteModal, setShowPrNoteModal] = useState(false);
  const [editablePlan, setEditablePlan] = useState(null);

  // ──────────────────────────────────────────────────────────────────────────
  // 1. FULL AI GENERATOR STATE (Captures all 15 parameters)
  // ──────────────────────────────────────────────────────────────────────────
  const [aiOrigin, setAiOrigin] = useState('');
  const [aiDestination, setAiDestination] = useState('');
  const [aiStartDate, setAiStartDate] = useState('');
  const [aiEndDate, setAiEndDate] = useState('');
  const [aiDays, setAiDays] = useState(3);
  const [aiBudget, setAiBudget] = useState(50000);
  const [aiIsRoundTrip, setAiIsRoundTrip] = useState(false);
  const [aiNumTravelers, setAiNumTravelers] = useState(1);
  const [aiTravelersMap, setAiTravelersMap] = useState([
    {
      gender: user?.gender || user?.sex || 'Male',
      age: user?.age || 26,
      name: user?.name || 'Organizer',
      dietary: user?.dietaryPreference || 'Any',
      isOrganizer: true,
    },
  ]);

  // Sync Slot 1 and travel preferences with logged in user profile
  useEffect(() => {
    if (user) {
      setAiTravelersMap((prev) => {
        const updated = [...prev];
        updated[0] = {
          gender: user.gender || user.sex || 'Male',
          age: user.age || 26,
          name: user.name || 'Organizer',
          dietary: user.dietaryPreference || 'Any',
          interests: user.interests || [],
          isOrganizer: true,
        };
        return updated;
      });
      if (user.location) {
        setAiOrigin((prev) => prev ? prev : user.location);
        setManualOrigin((prev) => prev ? prev : user.location);
      }
      if (user.dietaryPreference && user.dietaryPreference !== 'None') {
        setAiDietary((prev) => (prev === 'Any' || !prev) ? user.dietaryPreference : prev);
      }
      if (Array.isArray(user.interests) && user.interests.length > 0) {
        setAiPreferences((prev) => {
          if (!prev || prev.length === 0 || (prev.length === 2 && prev[0] === 'Nature' && prev[1] === 'Local Culture')) {
            return user.interests;
          }
          return prev;
        });
      }
    }
  }, [user]);
  const [aiSpecialReqs, setAiSpecialReqs] = useState([]);
  const [aiOtherReq, setAiOtherReq] = useState('');
  const [aiTravelStyles, setAiTravelStyles] = useState(['Relaxation']);
  const [aiPreferences, setAiPreferences] = useState(['Nature', 'Local Culture']);
  const [aiPacing, setAiPacing] = useState('Moderate');
  const [aiDietary, setAiDietary] = useState('Any');
  const [aiTransportMode, setAiTransportMode] = useState('Private Car');

  const [aiPreviews, setAiPreviews] = useState([]);
  const [aiSelectedPlan, setAiSelectedPlan] = useState(null);
  const [cachedAiPlans, setCachedAiPlans] = useState({});
  const [aiProgress, setAiProgress] = useState(0);
  const [aiProgressStage, setAiProgressStage] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [showSocialModalTrip, setShowSocialModalTrip] = useState(null);

  const specialOptions = ['Wheelchair Accessible', 'Vegetarian Food', 'Pet Friendly', 'Senior Citizen Support'];
  const styleOptions = ['Luxury', 'Adventure', 'Cultural', 'Relaxation', 'Budget', 'Family'];
  const prefOptions = ['Nature', 'Nightlife', 'Historical', 'Shopping', 'Food Tour', 'Local Culture'];
  const transportOptions = ['Flight', 'Train', 'Bus', 'Private Car', 'Mixed'];
  const pacingOptions = ['Relaxed', 'Moderate', 'Hectic'];
  const dietaryOptions = ['Any', 'Strictly Vegetarian', 'Vegan', 'Jain', 'Halal', 'Non-Vegetarian'];

  const handleNumTravelersChange = (e) => {
    const val = parseInt(e.target.value) || 1;
    setAiNumTravelers(val);
    const newArr = [...aiTravelersMap];
    while (newArr.length < val) newArr.push({ gender: 'Male', age: 25 });
    while (newArr.length > val) newArr.pop();
    setAiTravelersMap(newArr);
  };

  const updateTraveler = (index, field, value) => {
    const newArr = [...aiTravelersMap];
    newArr[index][field] = value;
    setAiTravelersMap(newArr);
  };

  const toggleArray = (arr, setArr, val) => {
    if (arr.includes(val)) setArr(arr.filter(i => i !== val));
    else setArr([...arr, val]);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 2. UNIFIED MANUAL BUILDER STATE
  // ──────────────────────────────────────────────────────────────────────────
  const [manualTitle, setManualTitle] = useState('');
  const [manualOrigin, setManualOrigin] = useState('');
  const [manualDestination, setManualDestination] = useState('');
  const [manualStartDate, setManualStartDate] = useState('');
  const [manualTravelers, setManualTravelers] = useState(2);
  const [manualBudget, setManualBudget] = useState(40000);
  const [manualTravelStyle, setManualTravelStyle] = useState('Relaxation');
  const [manualTripType, setManualTripType] = useState('Scenic Road Trip');
  const [manualTransportMode, setManualTransportMode] = useState('Private Car');
  const [manualPacing, setManualPacing] = useState('Moderate');
  const [manualDietary, setManualDietary] = useState('Any');
  const [manualCoverImage, setManualCoverImage] = useState('');
  const [manualOverview, setManualOverview] = useState('');
  const [manualPreferences, setManualPreferences] = useState(['Nature', 'Local Culture']);
  const [manualSpecialReqs, setManualSpecialReqs] = useState('');

  // Auto-calculate end date helper: startDate + (numDays - 1)
  const calculateEndDate = (startDateStr, numDays) => {
    if (!startDateStr || !numDays || numDays < 1) return '';
    const d = new Date(startDateStr);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + (parseInt(numDays, 10) - 1));
    return d.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const computedAiEndDate = calculateEndDate(aiStartDate, aiDays);
  const computedManualEndDate = calculateEndDate(manualStartDate, 1);

  const [manualDays, setManualDays] = useState([
    {
      dayNumber: 1,
      title: 'Arrival & Scenic Exploration',
      route: 'Origin -> Destination',
      distance: '150 km',
      travelTime: '4 hrs',
      weatherConditions: 'Clear and Sunny',
      tempHigh: '24°C',
      tempLow: '14°C',
      experienceDescription: 'Misty mountain drive arriving at destination with local tea tasting.',
      dailyPacing: 'Moderate',
      hotelName: 'Valley Heritage Resort',
      hotelImageUrl: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      hotelCost: 4500,
      hotelBookingPlatform: 'Booking.com',
      hotelRecommendationReason: 'Superb heated rooms with panoramic mountain balcony views.',
      transportMode: 'Private Cab',
      transportCost: 3500,
      transportBookingPlatform: 'Local Car Rental',
      dailyActivities: [
        {
          name: 'Historic Monastery & Sunset Point',
          imageUrl: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800',
          estimatedINR: 500,
          costINR: 500,
          time: '4:00 PM',
          location: 'Old Town',
          description: 'Explore century-old architecture and breathtaking valley sunset.',
        }
      ],
      costBreakdown: {
        transportINR: 3500,
        stayINR: 4500,
        foodINR: 1200,
        activitiesINR: 500,
      }
    }
  ]);

  const addManualActivity = (dIdx) => {
    const updated = [...manualDays];
    if (!updated[dIdx].dailyActivities) updated[dIdx].dailyActivities = [];
    updated[dIdx].dailyActivities.push({
      name: '',
      title: '',
      time: '10:00 AM',
      estimatedINR: 500,
      costINR: 500,
      location: manualDestination || '',
      description: '',
      imageUrl: '',
    });
    setManualDays(updated);
  };

  const removeManualActivity = (dIdx, aIdx) => {
    const updated = [...manualDays];
    updated[dIdx].dailyActivities = updated[dIdx].dailyActivities.filter((_, i) => i !== aIdx);
    setManualDays(updated);
  };

  const updateManualActivity = (dIdx, aIdx, field, val) => {
    const updated = [...manualDays];
    const acts = [...updated[dIdx].dailyActivities];
    acts[aIdx] = { ...acts[aIdx], [field]: val };
    if (field === 'name') acts[aIdx].title = val;
    if (field === 'title') acts[aIdx].name = val;
    if (field === 'estimatedINR') acts[aIdx].costINR = val;
    if (field === 'costINR') acts[aIdx].estimatedINR = val;
    updated[dIdx].dailyActivities = acts;
    setManualDays(updated);
  };

  const [manualPreviewPlan, setManualPreviewPlan] = useState(null);


  // ──────────────────────────────────────────────────────────────────────────
  // 3. TRIP CONTINUATION / SHARED TRIP EDITING / COMPANION PR FORK
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const formatDbTripToEditable = (t) => ({
      id: t.id,
      title: t.title || t.destination,
      destination: t.destination,
      origin: t.origin,
      startDate: t.startDate,
      endDate: t.endDate,
      totalDays: t.totalDays || (t.days ? t.days.length : 3),
      travelers: t.travelers || 2,
      budget: t.budget || 50000,
      travelStyle: t.travelStyle || 'Relaxation',
      tripType: t.tripType || 'Curated Expedition',
      coverImageUrl: t.coverImageUrl || '',
      summary: {
        destination: t.destination,
        imageUrl: t.coverImageUrl,
        tripType: t.tripType,
        totalDays: t.totalDays,
        companions: t.travelers,
      },
      days: (t.days || []).map((d, i) => ({
        dayNumber: d.dayNumber || i + 1,
        date: d.date || null,
        title: d.title || `Day ${i + 1}`,
        route: d.route || '',
        weatherConditions: d.weatherConditions || 'Sunny',
        transportMode: d.transportMode || 'Private Car',
        transportCost: Number(d.transportCost) || 0,
        transportBookingPlatform: d.transportBookingPlatform || '',
        hotelName: d.hotelName || '',
        hotelImageUrl: d.hotelImageUrl || '',
        hotelCost: Number(d.hotelCost) || 0,
        hotelBookingPlatform: d.hotelBookingPlatform || '',
        experienceDescription: d.experienceDescription || '',
        dailyActivities: (d.dailyActivities || []).map((a) => ({
          time: a.time || '10:00',
          name: a.name || a.title || 'Activity',
          title: a.title || a.name || 'Activity',
          description: a.description || '',
          location: a.location || '',
          estimatedINR: a.estimatedINR || a.costINR || 0,
          costINR: a.costINR || a.estimatedINR || 0,
          imageUrl: a.imageUrl || '',
        })),
      })),
      costBreakdownFull: t.costBreakdownFull || {
        transportINR: Math.round(Number(t.budget || 50000) * 0.25),
        stayINR: Math.round(Number(t.budget || 50000) * 0.45),
        foodINR: Math.round(Number(t.budget || 50000) * 0.15),
        activitiesINR: Math.round(Number(t.budget || 50000) * 0.15),
      },
      totalCostSummary: t.totalCostSummary || { safeCostINR: t.budget },
      travelInsights: t.travelInsights || {
        bestTimeToVisit: 'October to March',
        currency: 'INR',
        localEtiquette: 'Respect local customs.',
      },
    });

    const initTripData = async () => {
      if (draftIdParam) {
        setLoading(true);
        setLoadingText('Loading Draft Itinerary from Database...');
        try {
          const draft = await tripsApi.getDraft(draftIdParam);
          if (draft && isMounted) {
            setEditingDraftId(draftIdParam);
            setEditablePlan(formatDbTripToEditable(draft));
          }
        } catch (err) {
          console.error('Failed to load draft:', err);
          alert('Could not load draft. You can create a new trip instead.');
        } finally {
          if (isMounted) setLoading(false);
        }
      } else if (sharedTripIdParam) {
        setLoading(true);
        setLoadingText('Loading Shared Expedition from Database...');
        try {
          const trip = await tripsApi.getSharedTrip(sharedTripIdParam);
          if (trip && isMounted) {
            setEditingSharedTripId(sharedTripIdParam);
            setEditablePlan(formatDbTripToEditable(trip));
          }
        } catch (err) {
          console.error('Failed to load shared expedition:', err);
          alert('Could not load shared expedition.');
        } finally {
          if (isMounted) setLoading(false);
        }
      } else if (forkTripParam) {
        setLoading(true);
        setLoadingText('Loading Baseline Expedition for Proposed Changes (PR)...');
        try {
          const trip = await tripsApi.getSharedTrip(forkTripParam);
          if (trip && isMounted) {
            setProposeChangesTripId(forkTripParam);
            setPrOrganizerName(trip.user?.name || 'the Organizer');
            setEditablePlan(formatDbTripToEditable(trip));
          }
        } catch (err) {
          console.error('Failed to load trip for proposal:', err);
          alert('Could not load expedition for proposed changes.');
        } finally {
          if (isMounted) setLoading(false);
        }
      }
    };

    initTripData();
    return () => {
      isMounted = false;
    };
  }, [draftIdParam, sharedTripIdParam, forkTripParam]);

  // ──────────────────────────────────────────────────────────────────────────
  // 4. GENERATE AI PLAN (Calling Python AI Planner Engine with Live % Progress)
  // ──────────────────────────────────────────────────────────────────────────
  const generateAiPlan = async () => {
    if (!aiOrigin) return alert('Please enter your departure origin city.');
    if (parseInt(aiDays) > 8) return alert('AI generation is optimized for 8 days or less.');

    const todayStr = new Date().toISOString().split('T')[0];
    if (aiStartDate && aiStartDate < todayStr) {
      return alert('Travel start date cannot be in the past. Please select today or a future date.');
    }

    setIsGeneratingAi(true);
    setAiProgress(5);
    setAiProgressStage('Analyzing travel profile & origin parameters...');

    // Live dynamic percentage counter
    let currentPct = 5;
    const progressInterval = setInterval(() => {
      currentPct += Math.floor(Math.random() * 5) + 3; // +3% to 7%
      if (currentPct > 92) {
        currentPct = 92; // Cap at 92% until server finishes
      }
      setAiProgress(currentPct);

      if (currentPct < 25) {
        setAiProgressStage('Analyzing traveler preferences, budget & dietary constraints...');
      } else if (currentPct < 55) {
        setAiProgressStage('Discovering matching destinations & seasonal trends...');
      } else if (currentPct < 75) {
        setAiProgressStage('Architecting day-by-day itinerary & transit routes...');
      } else if (currentPct < 90) {
        setAiProgressStage('Optimizing hotel accommodations, stays & dining...');
      } else {
        setAiProgressStage('Finalizing high-fidelity travel plans...');
      }
    }, 650);

    try {
      // Build rich, structured traveler demographics for prompt intelligence
      const travelerDetailsStr = aiTravelersMap.map((t, i) => {
        const title = t.isOrganizer ? `Person 1 (Organizer - ${t.name || user?.name || 'Lead'})` : `Person ${i + 1}${t.name ? ` (${t.name})` : ''}`;
        const diet = t.dietary && t.dietary !== 'Any' ? `, Dietary: ${t.dietary}` : '';
        return `${title}: ${t.gender || 'Adult'}, ${t.age || 25} yrs${diet}`;
      }).join('; ');

      const finalReq = aiSpecialReqs.join(', ') + (aiOtherReq ? ` | Other: ${aiOtherReq}` : '');
      const computedAiEnd = calculateEndDate(aiStartDate, aiDays);

      const payload = {
        origin: aiOrigin.trim(),
        days: parseInt(aiDays, 10),
        budget: parseInt(aiBudget, 10),
        travelers: parseInt(aiNumTravelers, 10),
        travelStyle: (aiTravelStyles && aiTravelStyles.length > 0) ? aiTravelStyles.join(', ') : 'Mixed',
        isRoundTrip: Boolean(aiIsRoundTrip),
        travelerDetails: travelerDetailsStr,
        specialRequirements: finalReq || 'None',
        preferences: aiPreferences || [],
        transportMode: aiTransportMode || 'Private Car',
        pacing: aiPacing || 'Moderate',
        dietary: aiDietary || 'Any',
        additionalInformation: aiOtherReq ? aiOtherReq.trim() : 'None',
      };
      if (aiDestination && aiDestination.trim()) {
        payload.destination = aiDestination.trim();
      }
      if (aiStartDate) {
        payload.startDate = new Date(aiStartDate).toLocaleDateString('en-GB');
      }
      if (computedAiEnd) {
        payload.endDate = new Date(computedAiEnd).toLocaleDateString('en-GB');
      }

      const aiBaseUrl = process.env.REACT_APP_AI_PLANNER_URL || 'https://syncandexplore-ai-trip-planner-z7qy.onrender.com';
      const res = await axios.post(`${aiBaseUrl}/api/v1/plans/generate`, payload);

      let finalData = res.data?.data || res.data;
      if (typeof finalData === 'string') {
        try { finalData = JSON.parse(finalData); } catch (e) {}
      }

      if (finalData?.error === true) {
        clearInterval(progressInterval);
        setIsGeneratingAi(false);
        alert('Budget Warning: ' + finalData.message);
        return;
      }

      let previews = [];
      if (finalData.previews) {
        previews = finalData.previews;
      } else if (finalData.id && finalData.summary) {
        previews = [{ id: finalData.id, summary: finalData.summary }];
      } else {
        previews = [finalData];
      }

      // 1.1) Filter out any incomplete cards: Only show cards that have complete, valid data
      const validPreviews = previews.filter((option) => {
        if (!option || !option.id) return false;
        const s = option.summary;
        if (!s) return false;
        const dest = s.destination ? String(s.destination).trim() : '';
        if (!dest || dest.toLowerCase() === 'not applicable' || dest.toLowerCase() === 'unknown') {
          return false;
        }
        return true;
      });

      // Jump to 100% completion
      clearInterval(progressInterval);
      setAiProgress(100);
      setAiProgressStage('Complete! Presenting your tailored destinations...');

      // Pre-cache any plans that already have full itinerary data
      const initialCache = {};
      validPreviews.forEach((opt) => {
        const full = opt.fullPlan || (opt.itinerary || opt.days ? opt : null);
        if (full) {
          initialCache[opt.id] = normalizeAiPlan(full);
        }
      });
      if (Object.keys(initialCache).length > 0) {
        setCachedAiPlans((prev) => ({ ...prev, ...initialCache }));
      }

      await new Promise((resolve) => setTimeout(resolve, 400));
      setAiPreviews(validPreviews);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('AI Plan Error:', err);
      alert('AI Trip Planner encountered an error: ' + (err.response?.data?.message || err.message));
    } finally {
      clearInterval(progressInterval);
      setIsGeneratingAi(false);
    }
  };

  const handleSelectAiPreviewCard = async (planId) => {
    if (!planId) return;

    // Check plan cache first (instant 0ms switching between options)
    if (cachedAiPlans[planId]) {
      setAiSelectedPlan(cachedAiPlans[planId]);
      return;
    }

    // Check if the preview card itself already carries the full plan
    const previewMatch = aiPreviews.find((p) => p.id === planId);
    if (previewMatch && (previewMatch.fullPlan || previewMatch.itinerary || previewMatch.days)) {
      const pData = normalizeAiPlan(previewMatch.fullPlan || previewMatch);
      setCachedAiPlans((prev) => ({ ...prev, [planId]: pData }));
      setAiSelectedPlan(pData);
      return;
    }

    setLoading(true);
    setLoadingText('Fetching Detailed Itinerary...');
    try {
      const aiBaseUrl =
        process.env.REACT_APP_AI_PLANNER_URL ||
        (process.env.REACT_APP_AI_PLANNER_API_URL
          ? process.env.REACT_APP_AI_PLANNER_API_URL.replace('/api/v1/plans/generate', '')
          : 'http://localhost:4000');

      const res = await axios.get(`${aiBaseUrl}/api/v1/plans/${planId}`);
      const rawPlan = res.data?.data || res.data;
      if (!rawPlan) {
        throw new Error('Empty plan data received from server');
      }
      const fullPlan = normalizeAiPlan(rawPlan);
      setCachedAiPlans((prev) => ({ ...prev, [planId]: fullPlan }));
      setAiSelectedPlan(fullPlan);
    } catch (err) {
      console.error('Select plan error:', err);
      alert('Failed to retrieve full plan details. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Convert plan into server format
  const formatTripForBackend = (rawPlan, isAi = true) => {
    const plan = isAi ? normalizeAiPlan(rawPlan) : rawPlan;
    if (isAi && plan) {
      const summary = plan.summary || {};
      const costSummary = plan.totalCostSummary || {};
      const breakdown = plan.costBreakdownFull || {};
      const computedAiEnd = calculateEndDate(aiStartDate, aiDays);

      return {
        title: summary.tripTitle || `${summary.destination || 'Trip'} Odyssey`,
        destination: summary.destination || 'Travel Destination',
        origin: aiOrigin || 'Departure City',
        startDate: aiStartDate || null,
        endDate: computedAiEnd || null,
        totalDays: summary.totalDays || (plan.itinerary ? plan.itinerary.length : (plan.days ? plan.days.length : parseInt(aiDays, 10))),
        travelers: summary.companions || aiNumTravelers,
        budget: costSummary.safeCostINR || costSummary.estimatedTotalCostINR || aiBudget,
        travelStyle: summary.experienceType || summary.tripType || aiTravelStyles.join(', '),
        tripType: summary.tripType || 'Curated Expedition',
        coverImageUrl: summary.imageUrl && summary.imageUrl !== 'Not Applicable' ? summary.imageUrl : null,
        totalCostSummary: costSummary,
        costBreakdownFull: breakdown,
        travelInsights: plan.travelInsights || null,
        logistics: plan.logistics || null,
        survivalGuide: plan.survivalGuide || null,
        days: (plan.itinerary || plan.days || []).map((day, idx) => ({
          dayNumber: day.dayNumber || day.day || idx + 1,
          date: day.date || null,
          title: day.title || `Day ${idx + 1}`,
          route: day.route || (day.location ? day.location.route : null),
          distance: day.distance || null,
          travelTime: day.travelTime || null,
          weatherConditions: typeof day.predictedWeather === 'object' ? day.predictedWeather?.conditions : day.weatherConditions,
          tempHigh: typeof day.predictedWeather === 'object' ? day.predictedWeather?.temperatureHigh : null,
          tempLow: typeof day.predictedWeather === 'object' ? day.predictedWeather?.temperatureLow : null,
          transportMode: typeof day.transportation === 'object' ? day.transportation?.mode : (day.transportMode || null),
          transportCost: typeof day.transportation === 'object' ? Number(day.transportation?.costINR || 0) : Number(day.transportCost || 0),
          transportBookingPlatform: typeof day.transportation === 'object' ? day.transportation?.bookingPlatform : null,
          hotelName: typeof day.accommodation === 'object' ? day.accommodation?.hotelName : (day.hotelName || null),
          hotelImageUrl: typeof day.accommodation === 'object' ? day.accommodation?.imageUrl : (day.hotelImageUrl || null),
          hotelCost: typeof day.accommodation === 'object' ? Number(day.accommodation?.costINR || 0) : Number(day.hotelCost || 0),
          hotelBookingPlatform: typeof day.accommodation === 'object' ? day.accommodation?.bookingPlatform : null,
          hotelRecommendationReason: typeof day.accommodation === 'object' ? (day.accommodation?.whyRecommended || day.accommodation?.reason) : null,
          experienceDescription: typeof day.dayOverview === 'object' ? day.dayOverview?.description : (day.experienceDescription || null),
          dailyPacing: typeof day.dayOverview === 'object' ? day.dayOverview?.pacing : (day.dailyPacing || null),
          dailyActivities: (day.dailyActivities || day.activities || []).map((act) => ({
            name: act.name || act.title,
            imageUrl: act.imageUrl,
            estimatedINR: act.estimatedINR || act.costINR || 0,
            time: act.time || act.timing,
            description: act.description,
            location: act.location,
          })),
          costBreakdown: day.costBreakdown || null,
        })),
      };
    } else {
      // Manual or editable plan with full null safety
      const p = plan || editablePlan || {};
      const daysSource = (p.days && p.days.length > 0) ? p.days : (manualDays && manualDays.length > 0 ? manualDays : []);
      const computedManualEnd = calculateEndDate(manualStartDate, daysSource.length || 1);
      return {
        title: p.title || manualTitle || `${p.destination || manualDestination || 'Expedition'} Journey`,
        destination: p.destination || manualDestination || 'Destination',
        origin: p.origin || manualOrigin || 'Departure City',
        startDate: p.startDate || manualStartDate || null,
        endDate: p.endDate || computedManualEnd || null,
        totalDays: p.totalDays || daysSource.length || 1,
        travelers: Number(p.travelers || manualTravelers || 1),
        budget: Number(p.budget !== undefined ? p.budget : (manualBudget || 0)),
        travelStyle: p.travelStyle || manualTravelStyle || 'Balanced',
        tripType: p.tripType || manualTripType || 'Road Trip',
        coverImageUrl: p.coverImageUrl || manualCoverImage || null,
        totalCostSummary: p.totalCostSummary || { safeCostINR: p.budget || manualBudget || 0 },
        costBreakdownFull: p.costBreakdownFull || {
          transportINR: Math.round(Number(p.budget || manualBudget || 0) * 0.25),
          stayINR: Math.round(Number(p.budget || manualBudget || 0) * 0.45),
          foodINR: Math.round(Number(p.budget || manualBudget || 0) * 0.15),
          activitiesINR: Math.round(Number(p.budget || manualBudget || 0) * 0.15),
        },
        travelInsights: p.travelInsights || {
          bestTimeToVisit: 'Year-round travel season',
          currency: 'INR (Indian Rupee)',
          localEtiquette: 'Respect regional cultural traditions.',
        },
        logistics: p.logistics || null,
        survivalGuide: p.survivalGuide || null,
        days: daysSource.map((day, idx) => ({
          dayNumber: day.dayNumber || idx + 1,
          date: day.date || null,
          title: day.title || `Day ${idx + 1}`,
          route: day.route || null,
          distance: day.distance || null,
          travelTime: day.travelTime || null,
          weatherConditions: day.weatherConditions || null,
          tempHigh: day.tempHigh || null,
          tempLow: day.tempLow || null,
          transportMode: day.transportMode || null,
          transportCost: Number(day.transportCost) || null,
          transportBookingPlatform: day.transportBookingPlatform || null,
          hotelName: day.hotelName || null,
          hotelImageUrl: day.hotelImageUrl || null,
          hotelCost: Number(day.hotelCost) || null,
          hotelBookingPlatform: day.hotelBookingPlatform || null,
          hotelRecommendationReason: day.hotelRecommendationReason || null,
          experienceDescription: day.experienceDescription || null,
          dailyPacing: day.dailyPacing || null,
          dailyActivities: (day.dailyActivities || []).map((act) => ({
            name: act.name || act.title || 'Activity',
            imageUrl: act.imageUrl || null,
            estimatedINR: act.estimatedINR || act.costINR || 0,
            time: act.time || act.timing || '10:00',
            description: act.description || '',
            location: act.location || '',
          })),
          costBreakdown: day.costBreakdown || null,
        })),
      };
    }
  };

  // Save Draft (New or Existing Draft update)
  const handleSaveDraft = async (sourcePlan = null, isAi = false) => {
    setLoading(true);
    setLoadingText('Persisting Draft to PostgreSQL...');
    try {
      let payload;
      if (editablePlan) {
        payload = formatTripForBackend(editablePlan, false);
      } else if (isAi) {
        payload = formatTripForBackend(sourcePlan || aiSelectedPlan, true);
      } else {
        payload = formatTripForBackend(sourcePlan || manualPreviewPlan?.rawFormatted, false);
      }

      let res;
      if (editingDraftId) {
        res = await tripsApi.updateDraft(editingDraftId, payload);
      } else {
        res = await tripsApi.saveDraft(payload);
        if (res?.id) setEditingDraftId(res.id);
      }

      setSuccessModal({
        type: 'draft',
        trip: res,
        title: 'Draft Saved Successfully',
        message: 'Your itinerary and photos have been securely updated in your PostgreSQL drafts.',
      });
    } catch (err) {
      console.error('Draft Save Error:', err);
      alert(err.response?.data?.message || err.message || 'Failed to save draft.');
    } finally {
      setLoading(false);
    }
  };

  // Publish and Share
  const handlePublishAndShare = async (sourcePlan = null, isAi = false) => {
    setLoading(true);
    setLoadingText('Publishing Journey & Generating Invite Code...');
    try {
      let payload;
      if (editablePlan) {
        payload = formatTripForBackend(editablePlan, false);
      } else if (isAi) {
        payload = formatTripForBackend(sourcePlan || aiSelectedPlan, true);
      } else {
        payload = formatTripForBackend(sourcePlan || manualPreviewPlan?.rawFormatted, false);
      }

      const res = await tripsApi.publishTrip(payload, editingDraftId);
      setSuccessModal({
        type: 'shared',
        trip: res,
        title: '🎉 Journey Published!',
        message: 'Your trip is live! Share your unique invite code with travel buddies so they can collaborate with you.',
      });
    } catch (err) {
      console.error('Publish Error:', err);
      alert(err.response?.data?.message || err.message || 'Failed to publish trip.');
    } finally {
      setLoading(false);
    }
  };

  // Save Live Shared Trip (Owner directly editing live trip)
  const handleSaveSharedTrip = async (planToSave = null) => {
    if (!editingSharedTripId) return;
    setLoading(true);
    setLoadingText('Updating Shared Expedition in Database...');
    try {
      const payload = formatTripForBackend(planToSave || editablePlan, false);
      const res = await tripsApi.updateSharedTrip(editingSharedTripId, payload);
      setSuccessModal({
        type: 'shared_updated',
        trip: res,
        title: 'Expedition Updated Successfully',
        message: 'Your modifications to the live trip itinerary and media have been persisted for all travelers.',
      });
    } catch (err) {
      console.error('Update Shared Trip Error:', err);
      alert(err.response?.data?.message || err.message || 'Failed to update shared expedition.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Proposed Changes (PR) to Organizer
  const handleSubmitProposal = async (note = '') => {
    if (!proposeChangesTripId) return;
    setLoading(true);
    setLoadingText('Submitting Proposed Changes (PR) to Organizer...');
    try {
      const proposedPlan = formatTripForBackend(editablePlan, false);
      const titleToSubmit = (prTitle || '').trim() || `Proposed modifications by ${user?.name || 'Companion'}`;
      const notesToSubmit = (note || prNote || '').trim() || 'Suggested enhancements to trip itinerary and accommodations';
      await tripsApi.createChangeRequest(proposeChangesTripId, {
        title: titleToSubmit,
        notes: notesToSubmit,
        proposedChanges: proposedPlan,
      });
      setSuccessModal({
        type: 'pr_submitted',
        trip: { id: proposeChangesTripId },
        title: '🎉 Proposed Changes (PR) Submitted!',
        message: `Your proposed itinerary changes have been sent to ${prOrganizerName || 'the organizer'}. They can review and merge your suggestions in their PR Review Hub.`,
      });
    } catch (err) {
      console.error('Submit PR Error:', err);
      alert(err.response?.data?.message || err.message || 'Failed to submit proposed changes.');
    } finally {
      setLoading(false);
    }
  };

  // Manual Day Operations
  const handleAddManualDay = () => {
    const nextNum = (editablePlan ? editablePlan.days.length : manualDays.length) + 1;
    const newDay = {
      dayNumber: nextNum,
      title: `Day ${nextNum} Exploration`,
      route: 'Scenic Excursion',
      distance: '40 km',
      travelTime: '2 hrs',
      weatherConditions: 'Pleasant & Mild',
      transportMode: 'Private Car',
      transportCost: 2000,
      transportBookingPlatform: 'Local Service',
      hotelName: 'Boutique Hotel',
      hotelImageUrl: '',
      hotelCost: 3500,
      hotelBookingPlatform: 'Direct Booking',
      experienceDescription: 'Explore regional highlights, artisan workshops, and local delicacies.',
      dailyActivities: [
        {
          name: 'Morning Cultural Walk',
          imageUrl: '',
          estimatedINR: 300,
          time: '10:00 AM',
          description: 'Guided tour of local landmarks.',
        }
      ],
    };

    if (editablePlan) {
      setEditablePlan({
        ...editablePlan,
        totalDays: nextNum,
        days: [...editablePlan.days, newDay]
      });
    } else {
      setManualDays([...manualDays, newDay]);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-gray-900 p-4 sm:p-6 lg:p-8">
      {loading && <Loader text={loadingText} />}

      {/* ── SUCCESS MODAL (WHITE THEME MATCHING UI) ── */}
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center space-y-6 shadow-2xl">
            <div className="w-16 h-16 bg-emerald-50 text-[#2A9D8F] rounded-full flex items-center justify-center mx-auto ring-8 ring-emerald-50/60">
              <CheckCircle2 className="w-8 h-8 text-[#2A9D8F]" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900">{successModal.title}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{successModal.message}</p>
            </div>

            {/* Direct Shareable Trip Link Box */}
            {successModal.trip?.id && (
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 space-y-3 text-left">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#2A9D8F] uppercase tracking-wider block">
                    Direct Shareable Trip Link
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">Anyone with link can join</span>
                </div>

                <div className="flex items-center justify-between bg-white px-3.5 py-2.5 rounded-xl border border-gray-200 gap-2">
                  <span className="text-xs font-mono text-gray-700 truncate select-all">
                    {`${window.location.origin}/dashboard/trip/${successModal.trip.id}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const directUrl = `${window.location.origin}/dashboard/trip/${successModal.trip.id}`;
                      navigator.clipboard.writeText(directUrl);
                      setCopiedCode(true);
                      setTimeout(() => setCopiedCode(false), 3000);
                    }}
                    className="px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] text-xs font-bold rounded-lg transition-colors shrink-0 flex items-center gap-1 cursor-pointer"
                  >
                    {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedCode ? 'Copied!' : 'Copy Link'}</span>
                  </button>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowSocialModalTrip(successModal.trip)}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 hover:opacity-95 text-white rounded-xl text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Share on Social Media (Instagram Card)</span>
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {successModal.trip?.id && (
                <button
                  type="button"
                  onClick={() => {
                    const tripId = successModal.trip.id;
                    setSuccessModal(null);
                    navigate(`/dashboard/trip/${tripId}`);
                  }}
                  className="flex-1 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white font-bold rounded-xl shadow-md text-xs cursor-pointer"
                >
                  {successModal.type === 'pr_submitted'
                    ? 'Return to Expedition'
                    : successModal.type === 'shared_updated'
                    ? 'View Updated Expedition'
                    : 'View Published Trip'}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSuccessModal(null);
                  navigate('/dashboard');
                }}
                className="flex-1 py-3 bg-white hover:bg-gray-50 text-gray-700 font-bold border border-gray-300 rounded-xl text-xs cursor-pointer"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PR NOTE MODAL (COMPANION SUBMIT CHANGE REQUEST) ── */}
      {showPrNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 max-w-lg w-full text-left space-y-5 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-50 text-[#2A9D8F] rounded-2xl flex items-center justify-center font-black">
                <GitPullRequest className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Submit Proposed Changes (PR)</h3>
                <p className="text-xs text-gray-500">
                  Send your suggested itinerary adjustments to {prOrganizerName || 'the trip organizer'}.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Change Request Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={prTitle}
                onChange={(e) => setPrTitle(e.target.value)}
                placeholder="e.g. Adjusted pacing and hotel bookings"
                className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2A9D8F] focus:bg-white font-medium"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Note to Organizer (Optional)
              </label>
              <textarea
                rows={3}
                value={prNote}
                onChange={(e) => setPrNote(e.target.value)}
                placeholder="e.g. Swapped hotel on Day 2 to beachside resort and adjusted transit route for better timing..."
                className="w-full p-3.5 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#2A9D8F] focus:bg-white resize-none"
              />
            </div>

            <div className="p-3 bg-teal-50/70 rounded-xl border border-teal-100 text-xs text-teal-800 leading-relaxed">
              💡 <strong>How PRs work:</strong> The organizer will receive a notification and can review your suggested changes in their Change Requests hub, and merge them with one click or with AI reconciliation.
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowPrNoteModal(false)}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPrNoteModal(false);
                  handleSubmitProposal(prNote);
                }}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
              >
                <GitPullRequest className="w-4 h-4" />
                <span>Confirm &amp; Submit Proposal</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Share Modal for Published Trips */}
      <SocialShareModal
        isOpen={!!showSocialModalTrip}
        onClose={() => setShowSocialModalTrip(null)}
        trip={showSocialModalTrip}
        organizer={{ fullName: user?.fullName || 'Trip Organizer', email: user?.email }}
      />

      {/* ── MAIN CONTAINER ── */}
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Top Header & Switcher (Hide when viewing plan) */}
        {!aiSelectedPlan && !manualPreviewPlan && !editablePlan && (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-gray-200">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight flex items-center gap-3">
                {currentMode === 'ai' ? (
                  <>
                    <Sparkles className="w-9 h-9 text-[#2A9D8F]" />
                    <span>AI Trip Planner</span>
                  </>
                ) : (
                  <>
                    <PenTool className="w-9 h-9 text-blue-600" />
                    <span>Create Custom Trip</span>
                  </>
                )}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {currentMode === 'ai'
                  ? 'Generate complete, hyper-curated multi-day itineraries powered by AI intelligence.'
                  : 'Design your custom travel itinerary manually with route, hotel, and activity planning.'}
              </p>
            </div>

            {/* Mode Toggle Switcher only shown if NOT forcedMode */}
            {!forcedMode && (
              <div className="bg-white p-1.5 rounded-2xl border border-gray-200 shadow-sm flex items-center">
                <button
                  onClick={() => setActiveMode('ai')}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    activeMode === 'ai'
                      ? 'bg-gradient-to-r from-[#2A9D8F] to-emerald-600 text-white shadow-md'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span>AI Itinerary Architect</span>
                </button>

                <button
                  onClick={() => setActiveMode('manual')}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    activeMode === 'manual'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <PenTool className="w-4 h-4" />
                  <span>Custom Journey Crafter</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─────────────────────────────────────────────────────────────────── */}
        {/* VIEW 1: DRAFT CONTINUATION IN EDITABLE MODE                         */}
        {/* ─────────────────────────────────────────────────────────────────── */}
        {editablePlan ? (
          <EditableTripView
            plan={editablePlan}
            setPlan={setEditablePlan}
            onBack={() => {
              setEditablePlan(null);
              if (proposeChangesTripId) {
                navigate(`/dashboard/trip/${proposeChangesTripId}`);
              } else if (editingSharedTripId) {
                navigate(`/dashboard/trip/${editingSharedTripId}`);
              } else {
                navigate('/dashboard');
              }
            }}
            onSaveDraft={() => handleSaveDraft(editablePlan, false)}
            onPublish={() => handlePublishAndShare(editablePlan, false)}
            onSaveSharedTrip={() => handleSaveSharedTrip(editablePlan)}
            onSubmitProposal={() => setShowPrNoteModal(true)}
            onAddDay={handleAddManualDay}
            mode={proposeChangesTripId ? 'pr' : editingSharedTripId ? 'shared' : 'draft'}
            organizerName={prOrganizerName}
          />
        ) : aiSelectedPlan ? (
          <TripDetailView
            result={aiSelectedPlan}
            onBack={() => setAiSelectedPlan(null)}
            onSaveDraft={() => handleSaveDraft(aiSelectedPlan, true)}
            onPublish={() => handlePublishAndShare(aiSelectedPlan, true)}
            isAi={true}
            allAiPreviews={aiPreviews}
            currentPlanId={aiSelectedPlan?.id}
            onSelectPlan={(id) => handleSelectAiPreviewCard(id)}
          />
        ) : manualPreviewPlan ? (
          <TripDetailView
            result={manualPreviewPlan}
            onBack={() => setManualPreviewPlan(null)}
            onSaveDraft={() => handleSaveDraft(manualPreviewPlan, false)}
            onPublish={() => handlePublishAndShare(manualPreviewPlan, false)}
            isAi={false}
          />
        ) : (
          <>
            {/* ───────────────────────────────────────────────────────────────── */}
            {/* MODE A: AI GENERATOR (WHITE THEME)                                */}
            {/* ───────────────────────────────────────────────────────────────── */}
            {currentMode === 'ai' && (
              <div className="space-y-8">
                {aiPreviews && aiPreviews.length > 0 ? (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Select Your Destination</h2>
                        <p className="text-xs text-gray-500 mt-1">Our AI generated tailored itineraries based on your parameters.</p>
                      </div>
                      <button
                        onClick={() => setAiPreviews(null)}
                        className="text-sm font-bold text-[#2A9D8F] hover:underline cursor-pointer"
                      >
                        &larr; Reconfigure Options
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {aiPreviews.map((option, idx) => {
                        const summary = option.summary || {};
                        return (
                          <div
                            key={idx}
                            onClick={() => handleSelectAiPreviewCard(option.id)}
                            className="bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 cursor-pointer group flex flex-col"
                          >
                            <div className="relative h-48 overflow-hidden bg-slate-100">
                              {summary.imageUrl && summary.imageUrl !== 'Not Applicable' ? (
                                <img
                                  src={summary.imageUrl}
                                  alt={summary.destination}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Compass className="w-12 h-12" />
                                </div>
                              )}
                              <div className="absolute top-3 right-3 px-3 py-1 bg-black/70 backdrop-blur-md rounded-full text-white text-xs font-semibold">
                                {summary.tripType || 'Trip'}
                              </div>
                            </div>

                            <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#2A9D8F] transition-colors">
                                  {summary.destination || 'Selected Destination'}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                  {summary.experienceType || 'A tailor-made adventure awaits.'}
                                </p>
                              </div>

                              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                                <div>
                                  <p className="text-[11px] text-gray-400 font-bold uppercase">Estimated Cost</p>
                                  <p className="text-base font-bold text-emerald-600">
                                    ₹{(summary.totalCostPerPersonINR || summary.estimatedTotalCostINR || aiBudget).toLocaleString()}
                                  </p>
                                </div>
                                <div className="w-9 h-9 rounded-full bg-teal-50 text-[#2A9D8F] flex items-center justify-center group-hover:bg-[#2A9D8F] group-hover:text-white transition-colors">
                                  <ArrowRight className="w-4 h-4" />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-8 max-w-4xl mx-auto">
                    {/* Section 1: Core Essentials */}
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                        <MapPin className="w-5 h-5 text-[#2A9D8F]" /> Trip Essentials
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                            Departure Origin City *
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. New Delhi, Mumbai"
                            value={aiOrigin}
                            onChange={(e) => setAiOrigin(e.target.value)}
                            className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2A9D8F]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 flex items-center justify-between">
                            <span>Destination (Optional)</span>
                            <span className="text-[10px] text-[#2A9D8F] font-semibold">✨ Blank for 3 surprises</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Manali, Goa, Bali (or blank)"
                            value={aiDestination}
                            onChange={(e) => setAiDestination(e.target.value)}
                            className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2A9D8F]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                            Start Date
                          </label>
                          <input
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={aiStartDate}
                            onChange={(e) => setAiStartDate(e.target.value)}
                            className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                            End Date (Auto-Calculated)
                          </label>
                          <div className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-700 font-semibold flex items-center justify-between">
                            <span>{computedAiEndDate ? formatDisplayDate(computedAiEndDate) : 'Select start date'}</span>
                            <span className="text-[11px] font-bold text-[#2A9D8F] bg-teal-50 px-2 py-0.5 rounded-lg border border-teal-100">
                              {aiDays} Days
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                            Trip Duration: {aiDays} Days
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="8"
                            value={aiDays}
                            onChange={(e) => setAiDays(e.target.value)}
                            className="w-full accent-[#2A9D8F]"
                          />
                          <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                            <span>1 Day</span>
                            <span>8 Days (Max)</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                            Approx Budget (INR): ₹{aiBudget.toLocaleString()}
                          </label>
                          <input
                            type="range"
                            min="10000"
                            max="500000"
                            step="5000"
                            value={aiBudget}
                            onChange={(e) => setAiBudget(parseInt(e.target.value))}
                            className="w-full accent-[#2A9D8F]"
                          />
                          <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                            <span>₹10,000</span>
                            <span>₹5,00,000</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Travelers */}
                    <div className="pt-6 border-t border-gray-100">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                        <Users className="w-5 h-5 text-[#2A9D8F]" /> Travelers Configuration
                      </h2>

                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-600">
                            Number of Travelers:
                          </label>
                          <select
                            value={aiNumTravelers}
                            onChange={handleNumTravelersChange}
                            className="px-4 py-2 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                          >
                            {[1, 2, 3, 4, 5, 6].map(n => (
                              <option key={n} value={n}>{n} Traveler{n > 1 ? 's' : ''}</option>
                            ))}
                          </select>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {aiTravelersMap.map((t, idx) => (
                            <div
                              key={idx}
                              className={`p-3.5 rounded-2xl border flex flex-col justify-between gap-2.5 transition-all ${
                                idx === 0
                                  ? 'bg-teal-50/70 border-teal-200 shadow-xs'
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-900">
                                  {idx === 0 ? `Person 1: ${user?.name || 'You'}` : `Person ${idx + 1}`}
                                </span>
                                {idx === 0 ? (
                                  <span className="px-2 py-0.5 bg-[#2A9D8F] text-white text-[10px] font-extrabold rounded-md uppercase tracking-wider shadow-xs">
                                    Organizer (Pre-filled)
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-gray-200/80 text-gray-600 text-[10px] font-bold rounded-md uppercase tracking-wider">
                                    Open Slot
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-200/60">
                                <span className="text-[11px] text-gray-500 font-medium">Demographics:</span>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={t.gender}
                                    onChange={(e) => updateTraveler(idx, 'gender', e.target.value)}
                                    className="text-xs bg-white text-gray-800 rounded-lg px-2 py-1 border border-gray-300"
                                  >
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Other</option>
                                  </select>
                                  <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={t.age}
                                    onChange={(e) => updateTraveler(idx, 'age', parseInt(e.target.value) || 20)}
                                    className="w-14 text-xs bg-white text-gray-800 rounded-lg px-2 py-1 border border-gray-300 text-center"
                                  />
                                  <span className="text-[10px] text-gray-500">yrs</span>
                                </div>
                              </div>
                              <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-200/60">
                                <span className="text-[11px] text-gray-500 font-medium">Dietary:</span>
                                <select
                                  value={t.dietary || 'Any'}
                                  onChange={(e) => updateTraveler(idx, 'dietary', e.target.value)}
                                  className="text-xs bg-white text-gray-800 rounded-lg px-2 py-1 border border-gray-300 max-w-[130px]"
                                >
                                  {dietaryOptions.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Preferences */}
                    <div className="pt-6 border-t border-gray-100">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-[#2A9D8F]" /> Travel Preferences
                      </h2>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                            Travel Styles
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {styleOptions.map(st => (
                              <button
                                key={st}
                                type="button"
                                onClick={() => toggleArray(aiTravelStyles, setAiTravelStyles, st)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                  aiTravelStyles.includes(st)
                                    ? 'bg-[#2A9D8F] text-white shadow-xs'
                                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                {st}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                              Transport Mode
                            </label>
                            <select
                              value={aiTransportMode}
                              onChange={(e) => setAiTransportMode(e.target.value)}
                              className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                            >
                              {transportOptions.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                              Daily Pacing
                            </label>
                            <select
                              value={aiPacing}
                              onChange={(e) => setAiPacing(e.target.value)}
                              className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                            >
                              {pacingOptions.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                              Dietary Needs
                            </label>
                            <select
                              value={aiDietary}
                              onChange={(e) => setAiDietary(e.target.value)}
                              className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                            >
                              {dietaryOptions.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="button"
                      onClick={generateAiPlan}
                      className="w-full py-4 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white font-black text-base rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="w-5 h-5" />
                      <span>Architect Itinerary with AI</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ───────────────────────────────────────────────────────────────── */}
            {/* MODE B: MANUAL BUILDER (WHITE THEME)                              */}
            {/* ───────────────────────────────────────────────────────────────── */}
            {currentMode === 'manual' && (
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-8 max-w-4xl mx-auto text-gray-900">
                <div className="border-b border-gray-100 pb-4">
                  <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2.5">
                    <PenTool className="w-6 h-6 text-blue-600" />
                    <span>Custom Journey Crafter</span>
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Design your travel itinerary manually with Cloudinary image uploads and PostgreSQL storage.
                  </p>
                </div>

                {/* Core Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      Trip Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Himalayan Adventure 2026"
                      value={manualTitle}
                      onChange={(e) => setManualTitle(e.target.value)}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      Primary Destination *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Manali, Himachal Pradesh"
                      value={manualDestination}
                      onChange={(e) => setManualDestination(e.target.value)}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      Start Date
                    </label>
                    <input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={manualStartDate}
                      onChange={(e) => setManualStartDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      End Date (Auto-Calculated)
                    </label>
                    <div className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-700 font-semibold flex items-center justify-between">
                      <span>{computedManualEndDate ? formatDisplayDate(computedManualEndDate) : 'Select start date'}</span>
                      <span className="text-[11px] font-bold text-[#2A9D8F] bg-teal-50 px-2 py-0.5 rounded-lg border border-teal-100">
                        {manualDays.length} Days
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      Estimated Budget (INR)
                    </label>
                    <input
                      type="number"
                      value={manualBudget}
                      onChange={(e) => setManualBudget(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                      Travelers
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={manualTravelers}
                      onChange={(e) => setManualTravelers(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-[#2A9D8F]"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <CloudinaryUploader
                      label="Cover Image (Upload via Cloudinary or URL)"
                      value={manualCoverImage}
                      onChange={setManualCoverImage}
                    />
                  </div>
                </div>

                {/* Day-by-Day Section */}
                <div className="space-y-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#2A9D8F]" />
                      <span>Day-by-Day Route &amp; Activities</span>
                    </h3>
                    <button
                      type="button"
                      onClick={handleAddManualDay}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] font-bold text-xs rounded-xl border border-teal-200 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Day</span>
                    </button>
                  </div>

                  {manualDays.map((day, dIdx) => (
                    <div key={dIdx} className="p-5 rounded-2xl bg-gray-50 border border-gray-200 space-y-4">
                      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                        <span className="font-bold text-sm text-gray-900">Day {day.dayNumber || dIdx + 1}</span>
                        {manualDays.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setManualDays(manualDays.filter((_, i) => i !== dIdx))}
                            className="text-rose-500 hover:text-rose-700 text-xs font-bold"
                          >
                            Remove Day
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Day Title</label>
                          <input
                            type="text"
                            value={day.title}
                            onChange={(e) => {
                              const updated = [...manualDays];
                              updated[dIdx].title = e.target.value;
                              setManualDays(updated);
                            }}
                            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Route / Area</label>
                          <input
                            type="text"
                            value={day.route}
                            onChange={(e) => {
                              const updated = [...manualDays];
                              updated[dIdx].route = e.target.value;
                              setManualDays(updated);
                            }}
                            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Stay / Hotel Name</label>
                          <input
                            type="text"
                            value={day.hotelName}
                            onChange={(e) => {
                              const updated = [...manualDays];
                              updated[dIdx].hotelName = e.target.value;
                              setManualDays(updated);
                            }}
                            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Stay Cost (INR)</label>
                          <input
                            type="number"
                            value={day.hotelCost}
                            onChange={(e) => {
                              const updated = [...manualDays];
                              updated[dIdx].hotelCost = Number(e.target.value);
                              setManualDays(updated);
                            }}
                            className="w-full px-3 py-2 bg-white rounded-xl border border-gray-300 text-xs text-gray-900"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <CloudinaryUploader
                            label="Hotel Photo (Upload via Cloudinary or URL)"
                            value={day.hotelImageUrl}
                            onChange={(url) => {
                              const updated = [...manualDays];
                              updated[dIdx].hotelImageUrl = url;
                              setManualDays(updated);
                            }}
                          />
                        </div>

                        {/* Daily Planned Activities Builder for this Day */}
                        <div className="sm:col-span-2 pt-4 border-t border-gray-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-[#2A9D8F]" />
                              <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                                Daily Activities ({day.dailyActivities?.length || 0})
                              </h5>
                            </div>
                            <button
                              type="button"
                              onClick={() => addManualActivity(dIdx)}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] rounded-lg text-xs font-bold border border-teal-200 transition-colors cursor-pointer"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Add Activity</span>
                            </button>
                          </div>

                          {(!day.dailyActivities || day.dailyActivities.length === 0) ? (
                            <div className="p-4 bg-white rounded-xl border border-dashed border-gray-200 text-center">
                              <p className="text-xs text-gray-400">No activities added for Day {day.dayNumber || dIdx + 1}. Click "+ Add Activity" to add tours, sightseeing, or meals.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {day.dailyActivities.map((act, aIdx) => (
                                <div key={aIdx} className="p-4 bg-white rounded-xl border border-gray-200 space-y-3 shadow-xs">
                                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                    <span className="text-xs font-bold text-[#2A9D8F]">Activity #{aIdx + 1}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeManualActivity(dIdx, aIdx)}
                                      className="text-rose-500 hover:text-rose-700 text-xs font-bold cursor-pointer"
                                    >
                                      Remove
                                    </button>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="sm:col-span-2">
                                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Activity Name / Title *</label>
                                      <input
                                        type="text"
                                        value={act.name || act.title || ''}
                                        onChange={(e) => updateManualActivity(dIdx, aIdx, 'name', e.target.value)}
                                        placeholder="e.g. Scuba Diving / Monastery Visit"
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Time (e.g. 10:00 AM)</label>
                                      <input
                                        type="text"
                                        value={act.time || ''}
                                        onChange={(e) => updateManualActivity(dIdx, aIdx, 'time', e.target.value)}
                                        placeholder="10:00 AM"
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Est. Cost (INR)</label>
                                      <input
                                        type="number"
                                        value={act.estimatedINR || act.costINR || ''}
                                        onChange={(e) => updateManualActivity(dIdx, aIdx, 'estimatedINR', Number(e.target.value))}
                                        placeholder="500"
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                                      />
                                    </div>

                                    <div className="sm:col-span-2">
                                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Location / Area</label>
                                      <input
                                        type="text"
                                        value={act.location || ''}
                                        onChange={(e) => updateManualActivity(dIdx, aIdx, 'location', e.target.value)}
                                        placeholder="e.g. Old Town / Marina Bay"
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                                      />
                                    </div>

                                    <div className="sm:col-span-3">
                                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Experience Description</label>
                                      <input
                                        type="text"
                                        value={act.description || ''}
                                        onChange={(e) => updateManualActivity(dIdx, aIdx, 'description', e.target.value)}
                                        placeholder="Brief description of the activity..."
                                        className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:border-[#2A9D8F]"
                                      />
                                    </div>

                                    <div className="sm:col-span-3">
                                      <CloudinaryUploader
                                        label="Activity Photo (Upload via Cloudinary or URL)"
                                        value={act.imageUrl || ''}
                                        onChange={(url) => updateManualActivity(dIdx, aIdx, 'imageUrl', url)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Persistence Actions */}
                <div className="flex flex-wrap items-center justify-end gap-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => handleSaveDraft(null, false)}
                    className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 font-bold text-sm rounded-xl transition-all shadow-xs"
                  >
                    Save Draft
                  </button>

                  <button
                    type="button"
                    onClick={() => handlePublishAndShare(null, false)}
                    className="px-6 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white font-bold text-sm rounded-xl shadow-md transition-all"
                  >
                    Publish &amp; Share
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ───────────────────────────────────────────────────────────────── */}
      {/* REAL-TIME AI GENERATION PROGRESS MODAL (LIVE % COUNTER)           */}
      {/* ───────────────────────────────────────────────────────────────── */}
      {isGeneratingAi && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 text-center space-y-6 animate-in zoom-in-95">
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              {/* Pulsating outer effect */}
              <div className="absolute inset-0 rounded-full border-4 border-teal-200 animate-ping opacity-40"></div>
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#2A9D8F] to-emerald-400 flex items-center justify-center text-white shadow-xl shadow-teal-500/20">
                <Sparkles className="w-9 h-9 animate-pulse" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2.5">
                <span className="text-3xl font-black text-gray-900 font-mono tracking-tight">
                  {aiProgress}%
                </span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A9D8F] bg-teal-50 px-2.5 py-0.5 rounded-full border border-teal-200">
                  {aiProgress === 100 ? 'Ready' : 'Architecting'}
                </span>
              </div>
              <h3 className="text-lg font-black text-gray-900">
                Architecting Your Travel Plan
              </h3>
              <p className="text-xs text-gray-500 font-medium min-h-[32px] flex items-center justify-center px-4 leading-relaxed">
                {aiProgressStage}
              </p>
            </div>

            {/* Dynamic Progress Bar */}
            <div className="w-full bg-gray-100 rounded-full h-3.5 p-0.5 border border-gray-200 overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#2A9D8F] via-[#238177] to-emerald-400 h-full rounded-full transition-all duration-500 shadow-xs"
                style={{ width: `${aiProgress}%` }}
              ></div>
            </div>

            <div className="flex items-center justify-between text-[11px] text-gray-400 font-bold uppercase tracking-wider px-1">
              <span>Origin Vector</span>
              <span>Daily Matrix</span>
              <span>Itinerary Ready</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ────────────────────────────────────────────────────────────────────────────
 * VIEW: EDITABLE TRIP VIEW (FOR CONTINUING DRAFTS - ALL FIELDS EDITABLE)
 * ────────────────────────────────────────────────────────────────────────────
 */
function EditableTripView({
  plan,
  setPlan,
  onBack,
  onSaveDraft,
  onPublish,
  onSaveSharedTrip,
  onSubmitProposal,
  onAddDay,
  mode = 'draft',
  organizerName = '',
}) {
  const [exportingPdf, setExportingPdf] = useState(false);

  const updateField = (field, val) => {
    setPlan(prev => ({ ...prev, [field]: val }));
  };

  const updateDayField = (dIdx, field, val) => {
    const newDays = [...plan.days];
    newDays[dIdx][field] = val;
    setPlan(prev => ({ ...prev, days: newDays }));
  };

  const updateActivityField = (dIdx, aIdx, field, val) => {
    const newDays = [...plan.days];
    const newActs = [...newDays[dIdx].dailyActivities];
    newActs[aIdx][field] = val;
    newDays[dIdx].dailyActivities = newActs;
    setPlan(prev => ({ ...prev, days: newDays }));
  };

  const addActivity = (dIdx) => {
    const newDays = [...plan.days];
    const newActs = [...(newDays[dIdx].dailyActivities || [])];
    newActs.push({
      time: '14:00',
      title: 'New Activity',
      description: 'Describe this activity...',
      location: plan.destination || 'Local',
      costINR: 500,
      imageUrl: '',
    });
    newDays[dIdx].dailyActivities = newActs;
    setPlan(prev => ({ ...prev, days: newDays }));
  };

  const removeActivity = (dIdx, aIdx) => {
    const newDays = [...plan.days];
    newDays[dIdx].dailyActivities = newDays[dIdx].dailyActivities.filter((_, i) => i !== aIdx);
    setPlan(prev => ({ ...prev, days: newDays }));
  };

  const removeDay = (dIdx) => {
    if (plan.days.length <= 1) return alert('Itinerary must have at least 1 day.');
    const newDays = plan.days.filter((_, i) => i !== dIdx);
    setPlan(prev => ({ ...prev, days: newDays, totalDays: newDays.length }));
  };

  // PDF Export
  const handleExportPDF = () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    setTimeout(() => {
      try {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(22);
        doc.setTextColor(42, 157, 143);
        doc.setFont('helvetica', 'bold');
        doc.text(plan.title || plan.destination || 'Trip Itinerary', 14, 20);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(`Destination: ${plan.destination} | Duration: ${plan.totalDays} Days | Budget: Rs. ${Number(plan.budget || 0).toLocaleString()}`, 14, 28);

        const rows = (plan.days || []).map((d, i) => [
          `Day ${d.dayNumber || i + 1}`,
          d.title || `Day ${i + 1}`,
          d.route || 'Local Sightseeing',
          d.hotelName || 'Stay',
          `Rs. ${(Number(d.transportCost || 0) + Number(d.hotelCost || 0)).toLocaleString()}`
        ]);

        autoTable(doc, {
          startY: 36,
          head: [['Day', 'Title', 'Route', 'Stay', 'Est. Cost']],
          body: rows,
          margin: { left: 14, right: 14 },
          headStyles: { fillColor: [42, 157, 143], textColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });

        doc.save(`${(plan.destination || 'Trip').replace(/\s+/g, '_')}_Itinerary.pdf`);
      } catch (err) {
        console.error(err);
      } finally {
        setExportingPdf(false);
      }
    }, 400);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Collaboration PR Banner */}
      {mode === 'pr' && (
        <div className="p-4 sm:p-5 bg-gradient-to-r from-teal-500/10 via-cyan-500/10 to-emerald-500/10 border border-teal-500/20 rounded-3xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-teal-500/20 text-[#2A9D8F] flex items-center justify-center font-black shrink-0">
              <GitPullRequest className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span>Collaborative Proposal (PR) Workspace</span>
                <span className="text-[10px] uppercase font-black px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full">Active</span>
              </h4>
              <p className="text-xs text-gray-600 mt-0.5">
                You are proposing changes to <span className="font-bold text-teal-800">{organizerName || 'the Organizer'}</span>'s expedition. Every day, hotel, transit mode, and photo can be modified.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onSubmitProposal}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all whitespace-nowrap cursor-pointer shrink-0"
          >
            <GitPullRequest className="w-4 h-4" />
            <span>Submit Proposed Changes (PR)</span>
          </button>
        </div>
      )}

      {/* Action Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold border border-gray-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{mode === 'pr' || mode === 'shared' ? 'Back to Expedition' : 'Back to Dashboard'}</span>
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleExportPDF}
            disabled={exportingPdf}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin text-[#2A9D8F]" /> : <Download className="w-4 h-4 text-[#2A9D8F]" />}
            <span>Export PDF</span>
          </button>

          {mode === 'pr' ? (
            <button
              onClick={onSubmitProposal}
              className="inline-flex items-center gap-1.5 px-6 py-2 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
            >
              <GitPullRequest className="w-4 h-4" />
              <span>Submit Proposed Changes (PR)</span>
            </button>
          ) : mode === 'shared' ? (
            <button
              onClick={onSaveSharedTrip}
              className="inline-flex items-center gap-1.5 px-6 py-2 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>Save Expedition Changes</span>
            </button>
          ) : (
            <>
              <button
                onClick={onSaveDraft}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 rounded-xl text-xs font-bold transition-all shadow-xs"
              >
                <Save className="w-4 h-4 text-gray-500" />
                <span>Save Changes</span>
              </button>

              <button
                onClick={onPublish}
                className="inline-flex items-center gap-1.5 px-6 py-2 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
              >
                <Share2 className="w-4 h-4" />
                <span>Publish &amp; Share</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Editable Hero Banner */}
      <div
        className="relative bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-xl overflow-hidden flex flex-col justify-end min-h-[420px] text-white"
        style={{
          backgroundImage: `url(${plan.coverImageUrl || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent pointer-events-none"></div>

        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 backdrop-blur-md border border-teal-400/30 text-teal-300 text-xs font-bold uppercase tracking-wider">
            {mode === 'pr' ? (
              <>
                <GitPullRequest className="w-3.5 h-3.5" />
                <span>PR Proposal Workspace &bull; Proposing to {organizerName || 'Organizer'}</span>
              </>
            ) : mode === 'shared' ? (
              <>
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Live Expedition Editor &bull; Updating Shared Itinerary</span>
              </>
            ) : (
              <>
                <Edit3 className="w-3.5 h-3.5" />
                <span>Draft Editor &bull; Fully Editable Itinerary</span>
              </>
            )}
          </div>

          <div className="space-y-3">
            <input
              type="text"
              value={plan.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Enter Trip Name..."
              className="w-full text-3xl sm:text-5xl font-black bg-white/10 backdrop-blur-md rounded-2xl px-4 py-2 text-white border border-white/20 focus:outline-none focus:border-[#2A9D8F]"
            />

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <label className="block text-teal-200 font-bold mb-1">Destination</label>
                <input
                  type="text"
                  value={plan.destination || ''}
                  onChange={(e) => updateField('destination', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 backdrop-blur-md rounded-xl text-white border border-white/20 font-bold"
                />
              </div>

              <div>
                <label className="block text-teal-200 font-bold mb-1">Budget (INR)</label>
                <input
                  type="number"
                  value={plan.budget || 50000}
                  onChange={(e) => updateField('budget', Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white/10 backdrop-blur-md rounded-xl text-white border border-white/20 font-bold"
                />
              </div>

              <div>
                <label className="block text-teal-200 font-bold mb-1">Travelers</label>
                <input
                  type="number"
                  min="1"
                  value={plan.travelers || 2}
                  onChange={(e) => updateField('travelers', Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white/10 backdrop-blur-md rounded-xl text-white border border-white/20 font-bold"
                />
              </div>

              <div>
                <label className="block text-teal-200 font-bold mb-1">Total Days</label>
                <span className="block px-3 py-2 bg-white/10 backdrop-blur-md rounded-xl text-white border border-white/20 font-bold">
                  {plan.days?.length || plan.totalDays} Days
                </span>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <CloudinaryUploader
              label="Cover Image Photo (Cloudinary Upload)"
              value={plan.coverImageUrl}
              onChange={(url) => updateField('coverImageUrl', url)}
              className="max-w-md"
            />
          </div>
        </div>
      </div>

      {/* Editable Days Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Day-by-Day Journey &amp; Stays</h2>
          <button
            type="button"
            onClick={onAddDay}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] font-bold text-xs rounded-xl border border-teal-200 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Add Day</span>
          </button>
        </div>

        <div className="space-y-6">
          {(plan.days || []).map((day, dIdx) => (
            <div key={dIdx} className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
              {/* Day Header */}
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                  <span className="w-9 h-9 rounded-2xl bg-[#2A9D8F] text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0">
                    {day.dayNumber || dIdx + 1}
                  </span>
                  <input
                    type="text"
                    value={day.title || ''}
                    onChange={(e) => updateDayField(dIdx, 'title', e.target.value)}
                    placeholder="Day Title..."
                    className="text-lg font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 flex-1 focus:outline-none focus:border-[#2A9D8F]"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={day.weatherConditions || ''}
                    onChange={(e) => updateDayField(dIdx, 'weatherConditions', e.target.value)}
                    placeholder="Weather (e.g. Sunny)"
                    className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5"
                  />
                  {plan.days.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDay(dIdx)}
                      className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Delete Day"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Transit & Stay Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Transit Card */}
                <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 uppercase">
                    <Car className="w-4 h-4 text-[#2A9D8F]" />
                    <span>Transit &amp; Route</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Mode</label>
                      <input
                        type="text"
                        value={day.transportMode || ''}
                        onChange={(e) => updateDayField(dIdx, 'transportMode', e.target.value)}
                        placeholder="Transport mode"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Cost (INR)</label>
                      <input
                        type="number"
                        value={day.transportCost || 0}
                        onChange={(e) => updateDayField(dIdx, 'transportCost', Number(e.target.value))}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Route Description</label>
                    <input
                      type="text"
                      value={day.route || ''}
                      onChange={(e) => updateDayField(dIdx, 'route', e.target.value)}
                      placeholder="e.g. Airport to Hotel"
                      className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Distance</label>
                      <input
                        type="text"
                        value={day.distance || ''}
                        onChange={(e) => updateDayField(dIdx, 'distance', e.target.value)}
                        placeholder="e.g. 45 km"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Travel Time</label>
                      <input
                        type="text"
                        value={day.travelTime || ''}
                        onChange={(e) => updateDayField(dIdx, 'travelTime', e.target.value)}
                        placeholder="e.g. 1h 30m"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Pacing</label>
                      <input
                        type="text"
                        value={day.dailyPacing || ''}
                        onChange={(e) => updateDayField(dIdx, 'dailyPacing', e.target.value)}
                        placeholder="e.g. Moderate"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Stay Card */}
                <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 uppercase">
                    <BedDouble className="w-4 h-4 text-amber-500" />
                    <span>Accommodation &amp; Hotel</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Hotel Name</label>
                      <input
                        type="text"
                        value={day.hotelName || ''}
                        onChange={(e) => updateDayField(dIdx, 'hotelName', e.target.value)}
                        placeholder="Hotel name"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Hotel Cost (INR)</label>
                      <input
                        type="number"
                        value={day.hotelCost || 0}
                        onChange={(e) => updateDayField(dIdx, 'hotelCost', Number(e.target.value))}
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] text-gray-500 font-bold block mb-0.5">Why Recommended / Rationale</label>
                    <input
                      type="text"
                      value={day.hotelRecommendationReason || ''}
                      onChange={(e) => updateDayField(dIdx, 'hotelRecommendationReason', e.target.value)}
                      placeholder="e.g. Mountain views, central location, eco-friendly"
                      className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
                    />
                  </div>

                  <CloudinaryUploader
                    label="Hotel Photo (Cloudinary Upload)"
                    value={day.hotelImageUrl}
                    onChange={(url) => updateDayField(dIdx, 'hotelImageUrl', url)}
                  />
                </div>
              </div>

              {/* Experience Description */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  Daily Experience Summary
                </label>
                <textarea
                  value={day.experienceDescription || ''}
                  onChange={(e) => updateDayField(dIdx, 'experienceDescription', e.target.value)}
                  placeholder="Describe the overall day's adventure..."
                  rows={2}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:border-[#2A9D8F]"
                />
              </div>

              {/* Activities List */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Activities ({(day.dailyActivities || []).length})
                  </span>
                  <button
                    type="button"
                    onClick={() => addActivity(dIdx)}
                    className="text-xs font-bold text-[#2A9D8F] hover:underline inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Activity
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(day.dailyActivities || []).map((act, aIdx) => (
                    <div key={aIdx} className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <input
                          type="text"
                          value={act.time || '10:00'}
                          onChange={(e) => updateActivityField(dIdx, aIdx, 'time', e.target.value)}
                          className="w-20 px-2 py-0.5 bg-white border border-gray-300 rounded-lg text-xs font-bold text-[#2A9D8F]"
                        />
                        <button
                          type="button"
                          onClick={() => removeActivity(dIdx, aIdx)}
                          className="text-rose-500 hover:text-rose-700 text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <input
                        type="text"
                        value={act.title || act.name || ''}
                        onChange={(e) => {
                          updateActivityField(dIdx, aIdx, 'title', e.target.value);
                          updateActivityField(dIdx, aIdx, 'name', e.target.value);
                        }}
                        placeholder="Activity Title"
                        className="w-full px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-bold text-gray-900"
                      />

                      <textarea
                        value={act.description || ''}
                        onChange={(e) => updateActivityField(dIdx, aIdx, 'description', e.target.value)}
                        placeholder="Activity description..."
                        rows={2}
                        className="w-full px-2.5 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700"
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={act.location || ''}
                          onChange={(e) => updateActivityField(dIdx, aIdx, 'location', e.target.value)}
                          placeholder="Location"
                          className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg text-[11px]"
                        />
                        <input
                          type="number"
                          value={act.costINR || act.estimatedINR || 0}
                          onChange={(e) => {
                            updateActivityField(dIdx, aIdx, 'costINR', Number(e.target.value));
                            updateActivityField(dIdx, aIdx, 'estimatedINR', Number(e.target.value));
                          }}
                          placeholder="Cost (INR)"
                          className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg text-[11px]"
                        />
                      </div>

                      <CloudinaryUploader
                        label="Activity Photo"
                        value={act.imageUrl}
                        onChange={(url) => updateActivityField(dIdx, aIdx, 'imageUrl', url)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * ────────────────────────────────────────────────────────────────────────────
 * VIEW: READ-ONLY / AI GENERATED TRIP DETAIL VIEW (MATCHING BUDGETPLANNER.JSX)
 * ────────────────────────────────────────────────────────────────────────────
 */
function TripDetailView({
  result,
  onBack,
  onSaveDraft,
  onPublish,
  isAi,
  allAiPreviews = [],
  currentPlanId,
  onSelectPlan,
}) {
  const [exportingPdf, setExportingPdf] = useState(false);
  const [bgImage, setBgImage] = useState('');

  const plan = normalizeAiPlan(result) || result || {};
  const itinerary = plan?.itinerary || plan?.days || [];
  const destination = plan?.summary?.destination || plan?.destination || 'Your Journey';
  const totalDays = plan?.summary?.totalDays || itinerary.length || 3;
  const travelers = plan?.summary?.companions || plan?.travelers || 1;
  const tripType = plan?.summary?.tripType || plan?.travelStyle || 'Curated Expedition';
  const safeCost = plan?.totalCostSummary?.safeCostINR || plan?.budget || 50000;
  const costBreakdown = plan?.costBreakdownFull || {};
  const costSummary = plan?.totalCostSummary || {};
  const travelInsights = plan?.travelInsights || {};
  const logistics = plan?.logistics || {};
  const survivalGuide = plan?.survivalGuide || {};

  useEffect(() => {
    if (plan?.summary?.imageUrl && plan.summary.imageUrl !== 'Not Applicable') {
      setBgImage(plan.summary.imageUrl);
      return;
    }
    if (plan?.coverImageUrl && plan.coverImageUrl !== 'Not Applicable') {
      setBgImage(plan.coverImageUrl);
      return;
    }
    setBgImage('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop');
  }, [plan]);

  // Secure Cost Calculations for Recharts PieChart
  let transport = 0, stay = 0, food = 0, activities = 0, misc = 0;
  if (costBreakdown && Object.keys(costBreakdown).length > 0) {
    const b = costBreakdown;
    transport = Number(b.transportINR || b.transport || (Number(b.interCityTransportINR || 0) + Number(b.intraCityTransportINR || 0))) || 0;
    stay = Number(b.stayINR || b.accommodation || b.stay) || 0;
    food = typeof b.foodBreakdownINR === 'object'
      ? Object.values(b.foodBreakdownINR).reduce((sum, v) => sum + (Number(v) || 0), 0)
      : Number(b.foodINR || b.food) || 0;
    activities = Number(b.activitiesINR || b.activities) || 0;
    misc = typeof b.hiddenCostsINR === 'object'
      ? Object.values(b.hiddenCostsINR).reduce((sum, v) => sum + (Number(v) || 0), 0)
      : Number(b.miscINR || b.misc || 0);
  } else {
    itinerary.forEach((day) => {
      const cb = day.costBreakdown || {};
      transport += Number(cb.transportINR || day.transportCost) || 0;
      stay += Number(cb.stayINR || day.hotelCost) || 0;
      food += Number(cb.foodINR) || 1200;
      activities += Number(cb.activitiesINR) || (day.dailyActivities || []).reduce((s, a) => s + (Number(a.estimatedINR || a.costINR) || 0), 0);
    });
  }

  const rawChartData = [
    { name: 'Transport', value: transport || 12000, color: '#2A9D8F' },
    { name: 'Stay', value: stay || 18000, color: '#E76F51' },
    { name: 'Food', value: food || 8000, color: '#F4A261' },
    { name: 'Activities', value: activities || 5000, color: '#3B82F6' },
    { name: 'Misc / Taxes', value: misc || 0, color: '#8B5CF6' },
  ];
  const chartData = rawChartData.filter(d => d.value > 0);

  const handleExportPDF = () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    setTimeout(() => {
      try {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(22);
        doc.setTextColor(42, 157, 143);
        doc.setFont('helvetica', 'bold');
        doc.text(destination, 14, 20);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`${tripType} | ${totalDays} Days | ${travelers} Travelers | Budget: Rs. ${Number(safeCost).toLocaleString()}`, 14, 28);

        const rows = itinerary.map((d, i) => [
          `Day ${i + 1}`,
          d.title || `Day ${i + 1}`,
          d.route || 'Local Sightseeing',
          typeof d.accommodation === 'object' ? (d.accommodation?.hotelName || 'Stay') : (d.hotelName || 'Stay'),
          `Rs. ${(Number(d.transportCost || 0) + Number(d.hotelCost || 0)).toLocaleString()}`
        ]);

        autoTable(doc, {
          startY: 36,
          head: [['Day', 'Title', 'Route', 'Stay', 'Est. Cost']],
          body: rows,
          margin: { left: 14, right: 14 },
          headStyles: { fillColor: [42, 157, 143], textColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
        });

        doc.save(`${destination.replace(/\s+/g, '_')}_Itinerary.pdf`);
      } catch (e) {
        console.error(e);
      } finally {
        setExportingPdf(false);
      }
    }, 400);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Top Bar with Destination Option Switcher */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-xl text-xs font-bold border border-gray-300 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 text-[#2A9D8F]" />
            <span>
              {allAiPreviews && allAiPreviews.length > 1
                ? `← Back to Options (${allAiPreviews.length} Available)`
                : '← Back to Destinations'}
            </span>
          </button>

          {/* Direct Destination Switcher Pills */}
          {allAiPreviews && allAiPreviews.length > 1 && (
            <div className="flex items-center gap-1.5 pl-2 sm:border-l sm:border-gray-200 flex-wrap">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider hidden md:inline">
                Compare Options:
              </span>
              {allAiPreviews.map((option, idx) => {
                const isSelected =
                  option.id === currentPlanId ||
                  option.summary?.destination === destination;
                return (
                  <button
                    key={option.id || idx}
                    type="button"
                    onClick={() => onSelectPlan && onSelectPlan(option.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1 border ${
                      isSelected
                        ? 'bg-[#2A9D8F] text-white border-[#2A9D8F] shadow-xs'
                        : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                  >
                    <MapPin className="w-3 h-3" />
                    <span>{option.summary?.destination || `Option ${idx + 1}`}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleExportPDF}
            disabled={exportingPdf}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl border border-gray-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin text-[#2A9D8F]" /> : <Download className="w-4 h-4 text-[#2A9D8F]" />}
            <span>Export PDF</span>
          </button>

          <button
            onClick={onSaveDraft}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 rounded-xl text-xs font-bold shadow-xs cursor-pointer"
          >
            <FileText className="w-4 h-4 text-gray-400" />
            <span>Save to Drafts</span>
          </button>

          <button
            onClick={onPublish}
            className="inline-flex items-center gap-1.5 px-6 py-2 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer"
          >
            <Share2 className="w-4 h-4" />
            <span>Publish &amp; Share</span>
          </button>
        </div>
      </div>

      {/* Hero Banner */}
      <div
        className="relative bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-xl overflow-hidden flex flex-col justify-end min-h-[420px] text-white"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent pointer-events-none"></div>

        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 backdrop-blur-md border border-teal-400/30 text-teal-300 text-xs font-bold uppercase tracking-wider">
            <Compass className="w-3.5 h-3.5" />
            <span>{isAi ? 'AI Tailored Itinerary' : 'Custom Journey'}</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-white drop-shadow-lg">
            {destination}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-200">
            <span className="flex items-center gap-1.5 font-bold text-teal-300">
              <Sparkles className="w-4 h-4" />
              <span>{tripType}</span>
            </span>

            <span className="flex items-center gap-1.5 font-semibold">
              <Calendar className="w-4 h-4 text-[#2A9D8F]" />
              <span>{totalDays} Days</span>
            </span>

            <span className="flex items-center gap-1.5 font-semibold">
              <Users className="w-4 h-4 text-[#2A9D8F]" />
              <span>{travelers} Traveler(s)</span>
            </span>

            <span className="flex items-center gap-1.5 font-extrabold text-emerald-300">
              <Wallet className="w-4 h-4" />
              <span>₹{Number(safeCost).toLocaleString()} Budget</span>
            </span>
          </div>
        </div>
      </div>

      {/* Days List */}
      <div className="space-y-6">
        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Day-by-Day Journey</h2>

        <div className="space-y-6">
          {itinerary.map((day, idx) => {
            const dailyActs = day.dailyActivities || day.activities || [];
            const weather = typeof day.predictedWeather === 'object'
              ? `${day.predictedWeather?.conditions || ''} ${day.predictedWeather?.temperatureHigh ? `(${day.predictedWeather.temperatureHigh})` : ''}`
              : (day.weatherConditions || '');

            const transCost = Number(day.transportCost || day.transportDetails?.estimatedINR || day.costBreakdown?.transportINR || (typeof day.transportation === 'object' ? day.transportation?.costINR : 0)) || 0;
            const transMode = day.transportMode || day.transportDetails?.mode || (typeof day.transportation === 'object' ? day.transportation?.mode : 'Local Sightseeing Transfer');
            const transPlatform = day.transportBookingPlatform || day.transportDetails?.bookingPlatform || (typeof day.transportation === 'object' ? day.transportation?.bookingPlatform : null);

            const stayCost = Number(day.hotelCost || day.accommodation?.estimatedINR || day.costBreakdown?.stayINR || 0);
            const stayName = day.hotelName || day.accommodation?.hotelName || 'Curated Stay';
            const stayPlatform = day.accommodation?.bookingPlatform || null;
            const stayWhy = day.accommodation?.whyRecommended || day.accommodation?.reason || day.hotelRecommendationReason || null;
            const expDesc = day.experienceDescription || day.description || (typeof day.dayOverview === 'object' ? day.dayOverview?.description : null);
            const pacing = day.dailyPacing || (typeof day.dayOverview === 'object' ? day.dayOverview?.pacing : null);

            return (
              <div key={idx} className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-2xl bg-[#2A9D8F] text-white flex items-center justify-center font-bold text-sm shadow-sm">
                      {day.dayNumber || day.day || idx + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{day.title || `Day ${idx + 1}`}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        {day.route && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span>{day.route}</span>
                          </p>
                        )}
                        {day.distance && <span className="text-xs text-gray-400 font-mono">({day.distance})</span>}
                        {day.travelTime && <span className="text-xs text-gray-400 font-mono">· ⏱ {day.travelTime}</span>}
                        {pacing && (
                          <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[10px] font-bold uppercase">
                            {pacing}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {weather && (
                    <span className="text-xs text-amber-800 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 font-medium flex items-center gap-1">
                      <span>☀️</span>
                      <span>{weather}</span>
                    </span>
                  )}
                </div>

                {/* Transit & Accommodation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase">
                      <span className="flex items-center gap-1.5"><Car className="w-4 h-4 text-[#2A9D8F]" /> Transit &amp; Route</span>
                      {transCost > 0 && <span className="font-mono text-emerald-600 font-bold">₹{transCost.toLocaleString()}</span>}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{transMode}</p>
                    {transPlatform && (
                      <span className="text-[11px] text-gray-400 block">Platform: {transPlatform}</span>
                    )}
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase">
                      <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-amber-500" /> Accommodation</span>
                      {stayCost > 0 && <span className="font-mono text-emerald-600 font-bold">₹{stayCost.toLocaleString()}</span>}
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{stayName}</p>
                    {stayWhy && (
                      <p className="text-[11px] text-gray-500 italic leading-relaxed">{stayWhy}</p>
                    )}
                    {stayPlatform && (
                      <span className="text-[11px] text-gray-400 block">Book via: {stayPlatform}</span>
                    )}
                  </div>
                </div>

                {/* Experience Narrative */}
                {expDesc && (
                  <div className="p-4 rounded-2xl bg-teal-50/40 border border-teal-100 text-xs text-teal-900 leading-relaxed">
                    <p className="font-semibold text-teal-800 mb-0.5">Day's Experience Overview:</p>
                    <p>{expDesc}</p>
                  </div>
                )}

                {/* Planned Activities */}
                {(() => {
                  const dailyActs = day.dailyActivities || day.activities || [];
                  if (!dailyActs.length) return null;
                  return (
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-black text-teal-800 uppercase tracking-wider flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          <span>Planned Activities ({dailyActs.length})</span>
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {dailyActs.map((act, actIdx) => {
                          const actName = typeof act === 'object' ? (act.name || act.title || act.activityName || 'Activity') : act;
                          const actDesc = typeof act === 'string' ? '' : (act.description || act.details || '');
                          const actCost = typeof act === 'object' ? (act.estimatedINR || act.costINR || act.cost || 0) : 0;
                          const actTime = typeof act === 'object' ? act.time : '';
                          const actImg = typeof act === 'object' && act.imageUrl ? act.imageUrl : null;

                          return (
                            <div
                              key={actIdx}
                              className="group relative bg-white rounded-2xl border border-gray-200 hover:border-teal-400 overflow-hidden transition-all shadow-xs hover:shadow-md flex flex-col"
                            >
                              {actImg && (
                                <div className="relative h-40 w-full overflow-hidden bg-slate-100">
                                  <img
                                    src={actImg}
                                    alt={actName}
                                    onError={(e) => {
                                      e.target.src = `https://picsum.photos/seed/${encodeURIComponent(actName)}/600/400`;
                                    }}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                  {actCost > 0 && (
                                    <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur-md border border-emerald-500/40 rounded-lg text-emerald-400 text-xs font-bold font-mono">
                                      ₹{Number(actCost).toLocaleString()}
                                    </div>
                                  )}
                                  {actTime && (
                                    <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-teal-900/80 backdrop-blur-md border border-teal-400/40 rounded-lg text-teal-200 text-[11px] font-bold flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      <span>{actTime}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="p-4 flex-1 flex flex-col justify-between space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-start gap-2.5">
                                    <CheckCircle2 className="w-4 h-4 text-[#2A9D8F] shrink-0 mt-0.5" />
                                    <div>
                                      <h5 className="text-sm font-bold text-gray-900 leading-snug">{actName}</h5>
                                      {act.location && (
                                        <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                          <MapPin className="w-3 h-3 text-gray-400" />
                                          <span>{act.location}</span>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {!actImg && actCost > 0 && (
                                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 font-mono shrink-0">
                                      ₹{Number(actCost).toLocaleString()}
                                    </span>
                                  )}
                                </div>

                                {actDesc && (
                                  <p className="text-xs text-gray-600 leading-relaxed pl-6">
                                    {actDesc}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Day Cost Breakdown (if present) */}
                {day.costBreakdown && (
                  <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between text-xs text-gray-500 gap-2">
                    <span className="font-semibold text-gray-700">Day Est. Total: ₹{((Number(day.costBreakdown.transportINR || 0) + Number(day.costBreakdown.stayINR || 0) + Number(day.costBreakdown.foodINR || 0) + Number(day.costBreakdown.activitiesINR || 0))).toLocaleString()}</span>
                    <div className="flex items-center gap-2 font-mono text-[11px]">
                      {day.costBreakdown.transportINR && <span>Transit: ₹{Number(day.costBreakdown.transportINR).toLocaleString()}</span>}
                      {day.costBreakdown.stayINR && <span>Stay: ₹{Number(day.costBreakdown.stayINR).toLocaleString()}</span>}
                      {day.costBreakdown.foodINR && <span>Food: ₹{Number(day.costBreakdown.foodINR).toLocaleString()}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Financial & Budget Allocation */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-6">
        <div className="border-b border-gray-100 pb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#2A9D8F]" />
              <span>Financial &amp; Budget Allocation</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Comprehensive expense distribution across categories</p>
          </div>
          {costSummary.safeCostINR && (
            <div className="text-right">
              <span className="text-[11px] uppercase tracking-wider text-gray-400 font-bold block">Safe Target Budget</span>
              <span className="text-lg font-black text-emerald-600">₹{Number(costSummary.safeCostINR).toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Donut Chart with fixed safe dimensions */}
          <div className="lg:col-span-5 flex flex-col items-center justify-center">
            <div className="h-64 w-full min-w-0 relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val) => `₹${Number(val).toLocaleString()}`}
                    contentStyle={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#1e293b' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Total Est.</span>
                <span className="text-xl font-black text-gray-900 mt-0.5">₹{Number(safeCost).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Detailed Financial Category Table */}
          <div className="lg:col-span-7 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#2A9D8F]"></div>
                  <span className="text-xs font-bold text-gray-700">Transit &amp; Travel</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{transport.toLocaleString()}</span>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#E76F51]"></div>
                  <span className="text-xs font-bold text-gray-700">Stay &amp; Lodging</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{stay.toLocaleString()}</span>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#F4A261]"></div>
                  <span className="text-xs font-bold text-gray-700">Food &amp; Dining</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{food.toLocaleString()}</span>
              </div>

              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 rounded-full bg-[#3B82F6]"></div>
                  <span className="text-xs font-bold text-gray-700">Activities &amp; Tours</span>
                </div>
                <span className="text-sm font-black text-gray-900 font-mono">₹{activities.toLocaleString()}</span>
              </div>

              {misc > 0 && (
                <div className="sm:col-span-2 p-3.5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
                    <span className="text-xs font-bold text-gray-700">Permits, Entry Fees &amp; Misc</span>
                  </div>
                  <span className="text-sm font-black text-gray-900 font-mono">₹{misc.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Granular Food & Dining Split */}
            {costBreakdown.foodBreakdownINR && typeof costBreakdown.foodBreakdownINR === 'object' && (
              <div className="p-4 rounded-2xl bg-amber-50/40 border border-amber-100 space-y-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-amber-900 block">
                  Detailed Daily Meal Allocation
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Breakfast</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.foodBreakdownINR.breakfast || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Lunch</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.foodBreakdownINR.lunch || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Dinner</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.foodBreakdownINR.dinner || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-amber-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Snacks &amp; Drinks</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.foodBreakdownINR.snacksAndDrinks || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Hidden Costs, Taxes & Permits Breakdown */}
            {costBreakdown.hiddenCostsINR && typeof costBreakdown.hiddenCostsINR === 'object' && (
              <div className="p-4 rounded-2xl bg-purple-50/40 border border-purple-100 space-y-2">
                <span className="text-[11px] uppercase tracking-wider font-bold text-purple-900 block">
                  Hidden Costs, Taxes &amp; Permits
                </span>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Tolls &amp; Taxes</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.hiddenCostsINR.tollsAndTaxes || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Tips &amp; Gratuities</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.hiddenCostsINR.tips || 0).toLocaleString()}</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-purple-200/60 text-center">
                    <span className="text-[10px] text-gray-500 block font-semibold">Permits</span>
                    <span className="text-xs font-bold text-gray-900 font-mono">₹{Number(costBreakdown.hiddenCostsINR.permits || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Range Breakdown Cards */}
            {(costSummary.minimumCostINR || costSummary.maxCostINR || costSummary.perPersonINR) && (
              <div className="grid grid-cols-3 gap-2.5 pt-2">
                {costSummary.minimumCostINR && (
                  <div className="p-3 bg-teal-50/60 rounded-xl border border-teal-100 text-center">
                    <span className="text-[10px] font-bold text-teal-700 uppercase block">Min Budget</span>
                    <span className="text-xs font-black text-gray-900">₹{costSummary.minimumCostINR.toLocaleString()}</span>
                  </div>
                )}
                {costSummary.safeCostINR && (
                  <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100 text-center">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase block">Recommended</span>
                    <span className="text-xs font-black text-emerald-800">₹{costSummary.safeCostINR.toLocaleString()}</span>
                  </div>
                )}
                {costSummary.perPersonINR && (
                  <div className="p-3 bg-sky-50/60 rounded-xl border border-sky-100 text-center">
                    <span className="text-[10px] font-bold text-sky-700 uppercase block">Per Traveler</span>
                    <span className="text-xs font-black text-sky-900">₹{costSummary.perPersonINR.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Travel Insights, Logistics & Survival Guide */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Travel Intelligence & Insights */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3">
            <Sparkles className="w-5 h-5 text-[#2A9D8F]" />
            <h3 className="text-lg font-bold text-gray-900">Curated Travel Intelligence</h3>
          </div>

          <div className="space-y-4">
            {travelInsights.bestExperiences && travelInsights.bestExperiences.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-teal-700 uppercase tracking-wider block">
                  Top Recommended Experiences
                </span>
                <div className="space-y-1.5">
                  {travelInsights.bestExperiences.map((exp, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
                      <span>{exp}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.hiddenGems && travelInsights.hiddenGems.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider block">
                  Hidden Gems &amp; Offbeat Spots
                </span>
                <div className="space-y-1.5">
                  {travelInsights.hiddenGems.map((gem, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <span>{gem}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.cautionPoints && travelInsights.cautionPoints.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-rose-600 uppercase tracking-wider block">
                  Important Caution Points
                </span>
                <div className="space-y-1.5">
                  {travelInsights.cautionPoints.map((pt, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {travelInsights.bestTimeToVisit && (
              <div className="space-y-1.5 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-teal-800 uppercase tracking-wider block">
                  Best Time to Visit
                </span>
                <p className="text-xs text-gray-700 bg-teal-50/50 p-2.5 rounded-xl border border-teal-100">
                  {travelInsights.bestTimeToVisit}
                </p>
              </div>
            )}

            {travelInsights.sustainabilityTips && travelInsights.sustainabilityTips.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">
                  Sustainability &amp; Cultural Etiquette
                </span>
                <div className="space-y-1.5">
                  {travelInsights.sustainabilityTips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Logistics & Safety Guide */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-5">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-3">
            <ShieldCheck className="w-5 h-5 text-[#2A9D8F]" />
            <h3 className="text-lg font-bold text-gray-900">Logistics &amp; Safety Guide</h3>
          </div>

          <div className="space-y-4">
            {(logistics.packingList || logistics.packingChecklist) && (logistics.packingList || logistics.packingChecklist).length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <ShoppingBag className="w-3.5 h-3.5 text-[#2A9D8F]" />
                  <span>Essential Packing Checklist</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {(logistics.packingList || logistics.packingChecklist).map((item, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(logistics.healthAndSafety || logistics.healthAndAltitudeProtocols) && (logistics.healthAndSafety || logistics.healthAndAltitudeProtocols).length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <HeartPulse className="w-3.5 h-3.5 text-rose-500" />
                  <span>Health &amp; Altitude Protocols</span>
                </span>
                <div className="space-y-1.5">
                  {(logistics.healthAndSafety || logistics.healthAndAltitudeProtocols).map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.scamWarnings && survivalGuide.scamWarnings.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
                  <span>Local Scam Advisories</span>
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.scamWarnings.map((scam, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                      <span>{scam}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.localAppsToDownload && survivalGuide.localAppsToDownload.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                  Recommended Local Apps
                </span>
                <div className="flex flex-wrap gap-2">
                  {survivalGuide.localAppsToDownload.map((app, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-xl text-xs font-medium text-blue-800">
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.emergencyContacts && survivalGuide.emergencyContacts.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-rose-700 uppercase tracking-wider block">
                  Emergency Contacts &amp; Helplines
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.emergencyContacts.map((contact, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-rose-900 bg-rose-50/60 p-2 rounded-xl border border-rose-100">
                      <PhoneCall className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                      <span>{contact}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {survivalGuide.culturalNorms && survivalGuide.culturalNorms.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-bold text-purple-700 uppercase tracking-wider block">
                  Cultural Norms &amp; Local Etiquette
                </span>
                <div className="space-y-1.5">
                  {survivalGuide.culturalNorms.map((norm, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
                      <span>{norm}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
